import { Vector3 } from 'three';
import { EAttributeUnit } from '../../../@types/AttributeTypes';
import { IWall } from '../../../models/abstract/Wall';
import { IAttribute } from '../../../models/standard/AttributeModel';
import DoorModel from '../../../models/standard/DoorModel';
import PassageModel from '../../../models/standard/PassageModel';
import PartitionWallModel from '../../../models/standard/PartitionWallModel';
import RadiatorModel from '../../../models/standard/RadiatorModel';
import StandardWallModel from '../../../models/standard/StandardWallModel';
import WindowModel from '../../../models/standard/WindowModel';
import { IMPERIAL_TO_METRIC, METRIC_SCALE_FACTORS } from '../../../utils/attributes/attributeUtils';
import { DefaultAttributeId, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { DEFAULT_RADIATOR_DEPTH, DEFAULT_WALL_DEPTH } from '../../../utils/constants';
import { PvSceneExport } from '../../structures/panoviewer/new/@interfaces/@v_1_0/SceneExport';
import { PvWall } from '../../structures/panoviewer/new/@interfaces/@v_1_0/Wall';
import { PvWallEdge } from '../../structures/panoviewer/new/@interfaces/@v_1_0/WallEdge';
import { PvCategory } from '../../structures/panoviewer/new/attributehandling/@v_1_0/PanoViewerAttributes';
import { calculateShapeCenterXZ, calculateSmallestWidth, convertAttributes, isWallStructureClosed, PANOVIEWER_TO_FLOORPLANNER_UNIT } from './utils';

const EPS = 1e-6;

type Line2D = {
  p: Vector3;
  dir: Vector3;
};

function signedArea2D(points: Vector3[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y - points[j].x * points[i].y;
  }
  return area / 2;
}

function intersectLines2D(a: Line2D, b: Line2D): Vector3 | null {
  const cross = a.dir.x * b.dir.y - a.dir.y * b.dir.x;
  if (Math.abs(cross) < EPS) return null;

  const t = ((b.p.x - a.p.x) * b.dir.y - (b.p.y - a.p.y) * b.dir.x) / cross;
  return new Vector3(a.p.x + t * a.dir.x, a.p.y + t * a.dir.y, 0);
}

function innerPolygonToCenterLine(innerPoints: Vector3[], wallDepths: number[]): Vector3[] {
  if (innerPoints.length < 3) return innerPoints.map((p) => p.clone());

  const area = signedArea2D(innerPoints);
  const isCW = area <= 0;

  const offsetLines: Line2D[] = innerPoints.map((p, i) => {
    const next = innerPoints[(i + 1) % innerPoints.length];
    const dir = new Vector3().subVectors(next, p).normalize();
    const currentWallDepth = wallDepths[i] ?? DEFAULT_WALL_DEPTH;
    const halfDepth = currentWallDepth / 2;

    const outwardNormal = isCW ? new Vector3(-dir.y, dir.x, 0) : new Vector3(dir.y, -dir.x, 0);

    return {
      p: p.clone().add(outwardNormal.multiplyScalar(halfDepth)),
      dir,
    };
  });

  const centerLinePoints: Vector3[] = [];
  for (let i = 0; i < innerPoints.length; i++) {
    const prev = offsetLines[(i - 1 + innerPoints.length) % innerPoints.length];
    const curr = offsetLines[i];
    const hit = intersectLines2D(prev, curr);
    centerLinePoints.push(hit ?? curr.p.clone());
  }

  return centerLinePoints;
}

function calculateCameraAwayPartitionLine(
  startPosition: Vector3,
  endPosition: Vector3,
  wallDepth: number,
): { centerStartPosition: Vector3; centerEndPosition: Vector3; modelStartPosition: Vector3; modelEndPosition: Vector3 } {
  const innerWallVector = new Vector3().subVectors(endPosition, startPosition);
  const innerWallDir = innerWallVector.lengthSq() > EPS ? innerWallVector.clone().normalize() : new Vector3(1, 0, 0);

  const leftNormal = new Vector3(-innerWallDir.y, innerWallDir.x, 0);
  const rightNormal = new Vector3(innerWallDir.y, -innerWallDir.x, 0);
  const wallMidpoint = new Vector3().addVectors(startPosition, endPosition).multiplyScalar(0.5);
  wallMidpoint.z = 0;

  const useLeftNormal = wallMidpoint.lengthSq() > EPS ? leftNormal.dot(wallMidpoint) >= rightNormal.dot(wallMidpoint) : false;
  const cameraAwayNormal = useLeftNormal ? leftNormal : rightNormal;
  const centerLineOffset = cameraAwayNormal.multiplyScalar(wallDepth / 2);

  const centerStartPosition = startPosition.clone().add(centerLineOffset);
  const centerEndPosition = endPosition.clone().add(centerLineOffset);

  return {
    centerStartPosition,
    centerEndPosition,
    modelStartPosition: useLeftNormal ? centerStartPosition : centerEndPosition,
    modelEndPosition: useLeftNormal ? centerEndPosition : centerStartPosition,
  };
}

function extractDimensionFromAttributes(attributes: IAttribute[], attributeId: DefaultAttributeId): number | undefined {
  const attr = attributes.find((a) => a.id === attributeId);
  if (attr && attr.value !== undefined && attr.value !== null && attr.value !== '') {
    const parsedValue = typeof attr.value === 'string' ? parseFloat(attr.value) : (attr.value as number);

    if (!isNaN(parsedValue)) {
      const unit = attr.unit as EAttributeUnit;
      if (unit && METRIC_SCALE_FACTORS[unit] !== undefined) {
        return parsedValue * METRIC_SCALE_FACTORS[unit]! * 10;
      } else if (unit && IMPERIAL_TO_METRIC[unit] !== undefined) {
        return parsedValue * IMPERIAL_TO_METRIC[unit]! * 10;
      }
      return parsedValue;
    }
  }
  return undefined;
}

function syncDimensionToAttributes(attributes: IAttribute[], attributeId: DefaultAttributeId, internalValue: number | undefined): void {
  if (internalValue === undefined) return;

  const attr = attributes.find((a) => a.id === attributeId);
  if (attr && (attr.value === undefined || attr.value === null || attr.value === '')) {
    const unit = attr.unit as EAttributeUnit;
    let displayValue = internalValue;

    if (unit && METRIC_SCALE_FACTORS[unit] !== undefined) {
      displayValue = internalValue / (METRIC_SCALE_FACTORS[unit]! * 10);
    } else if (unit && IMPERIAL_TO_METRIC[unit] !== undefined) {
      displayValue = internalValue / (IMPERIAL_TO_METRIC[unit]! * 10);
    }

    attr.value = String(Math.round(displayValue * 100) / 100);
  }
}

type WallImportItem = {
  wall: PvWall;
  wallAttributes: IAttribute[];
  wallDepth: number | undefined;
  startPosition: Vector3;
  endPosition: Vector3;
  shapes: NonNullable<PvWall['shapes']>;
};

function orderWallItemsIntoLoop(items: WallImportItem[]): WallImportItem[] {
  if (items.length <= 2) return items.map((i) => ({ ...i }));

  const remaining = items.map((i) => ({ ...i }));
  const ordered: WallImportItem[] = [];

  ordered.push(remaining.shift()!);

  while (remaining.length > 0) {
    // Ordering by edge ids
    const lastInsert = ordered[ordered.length - 1];
    const nextStartId = lastInsert.wall.startEdgeId;
    const nextInsertIndex = remaining.findIndex((remainingWall) => remainingWall.wall.endEdgeId === nextStartId);
    if (nextInsertIndex !== -1) {
      const next = remaining.splice(nextInsertIndex, 1)[0];
      ordered.push(next);
    }
    // Fallback -> Ordering by vector positions
    else {
      // Panic, we never should be here, take a brake
      console.error('orderWallItemsIntoLoop: Wall ids are not coherent', 'Ordered', ordered, 'Remaining', remaining, 'Last Insert', lastInsert);
      const lastEnd = ordered[ordered.length - 1].endPosition;
      const nextIdx = remaining.findIndex((item) => item.startPosition.distanceTo(lastEnd) < 1e-3 || item.endPosition.distanceTo(lastEnd) < 1e-3);

      if (nextIdx === -1) break;

      const next = remaining.splice(nextIdx, 1)[0];
      if (next.endPosition.distanceTo(lastEnd) < 1e-3) {
        const swapped: WallImportItem = {
          ...next,
          startPosition: next.endPosition.clone(),
          endPosition: next.startPosition.clone(),
        };
        ordered.push(swapped);
      } else {
        ordered.push(next);
      }
    }
  }

  return ordered;
}

export function importWalls(sceneExport: PvSceneExport): {
  walls: Array<IWall>;
  standardWalls: Array<StandardWallModel>;
  partitionWalls: Array<PartitionWallModel>;
  roomAttributes: Array<IAttribute>;
  floorAttributes: Array<IAttribute>;
  cameraOffset: Vector3;
  boundaryPoints: Vector3[];
} {
  const standardWalls: Array<StandardWallModel> = [];
  const partitionWalls: Array<PartitionWallModel> = [];
  let roomAttributes: Array<IAttribute> = [];
  let floorAttributes: Array<IAttribute> = [];
  let cameraHeight = PANOVIEWER_TO_FLOORPLANNER_UNIT;
  let collectedBoundaryPoints: Vector3[] = [];

  if (sceneExport.measurements?.cameraHeight !== undefined && sceneExport.measurements?.cameraHeight !== 0) {
    cameraHeight = sceneExport.measurements?.cameraHeight * 0.01;
  } else if (sceneExport.defaultCameraStandHeight !== undefined && sceneExport.defaultCameraStandHeight !== 0) {
    cameraHeight = sceneExport.defaultCameraStandHeight * 0.01;
  }

  let hasMainRoom = false;

  if (sceneExport.measurements.spaces) {
    for (let i = 0; i < sceneExport.measurements.spaces.length; i++) {
      const space = sceneExport.measurements.spaces[i];

      if (!space.walls || !space.edges || space.walls.length === 0) {
        continue;
      }

      const spaceRelatedWalls: Array<PvWall> = space.walls;
      const spaceRelatedWallEdges: Array<PvWallEdge> = space.edges;
      const wallsAreStandardWalls = isWallStructureClosed(spaceRelatedWalls);

      if (wallsAreStandardWalls) {
        if (hasMainRoom) continue;
        hasMainRoom = true;

        roomAttributes = convertAttributes(space.attributes ?? [], ObjectType.Room);

        if (space.floor && space.floor.attributes) {
          floorAttributes = convertAttributes(space.floor.attributes, ObjectType.Floor);
        }
      }

      const wallItems: WallImportItem[] = [];

      spaceRelatedWalls.forEach((wall) => {
        const wallRelatedStartEdge = spaceRelatedWallEdges.find((edge) => wall.startEdgeId === edge.uuid);
        const wallRelatedEndEdge = spaceRelatedWallEdges.find((edge) => wall.endEdgeId === edge.uuid);

        if (!wallRelatedStartEdge || !wallRelatedEndEdge) return;

        const wallAttributes = convertAttributes(wall.attributes ?? [], ObjectType.Wall);
        const wallDepth = extractDimensionFromAttributes(wallAttributes, DefaultAttributeId.WALL_THICKNESS);

        const endPosition = new Vector3(wallRelatedStartEdge.floorPosition.x * cameraHeight, -1 * wallRelatedStartEdge.floorPosition.z * cameraHeight, 0);
        const startPosition = new Vector3(wallRelatedEndEdge.floorPosition.x * cameraHeight, -1 * wallRelatedEndEdge.floorPosition.z * cameraHeight, 0);

        wallItems.push({
          wall,
          wallAttributes,
          wallDepth,
          startPosition,
          endPosition,
          shapes: wall.shapes ?? [],
        });
      });

      if (wallsAreStandardWalls) {
        const orderedItems = orderWallItemsIntoLoop(wallItems);

        if (orderedItems.length === 0) continue;

        const wallDepths = orderedItems.map((item) => item.wallDepth ?? DEFAULT_WALL_DEPTH);

        const innerPoints = orderedItems.map((item) => item.startPosition.clone());
        collectedBoundaryPoints = innerPoints.map((p) => p.clone());

        const centerLinePoints = innerPolygonToCenterLine(innerPoints, wallDepths);

        for (let idx = 0; idx < orderedItems.length; idx++) {
          const item = orderedItems[idx];
          const centerStart = centerLinePoints[idx].clone();
          const centerEnd = centerLinePoints[(idx + 1) % centerLinePoints.length].clone();

          const wallVector = new Vector3().subVectors(centerEnd, centerStart);
          const wallLengthSq = wallVector.lengthSq();

          const radiators: Array<RadiatorModel> = [];
          const window: Array<WindowModel> = [];
          const doors: Array<DoorModel> = [];
          const passages: Array<PassageModel> = [];

          item.shapes.forEach((shape) => {
            if (!shape.object) return;

            const shapeObject = shape.object as PvCategory;

            let objType: ObjectType | undefined;
            if (shapeObject.id === 3) objType = ObjectType.Window;
            if (shapeObject.id === 4) objType = ObjectType.Door;
            if (shapeObject.id === 5) objType = ObjectType.Radiator;
            if (shapeObject.id === 10) objType = ObjectType.Passage;

            const objectAttributes = convertAttributes(shape.attributes ?? [], objType);

            const rawLength = calculateSmallestWidth(shape.points);
            const scaledLength = rawLength ? rawLength * cameraHeight : undefined;
            const rawCenter = calculateShapeCenterXZ(shape.points);

            const posX = rawCenter ? rawCenter.x * cameraHeight : shape.position.x * cameraHeight;
            const posY = rawCenter ? -1 * rawCenter.z * cameraHeight : -1 * shape.position.z * cameraHeight;
            const rawShapePosition = new Vector3(posX, posY, 0);

            let snappedShapePosition = rawShapePosition.clone();
            if (wallLengthSq > 0) {
              const shapeToStartVector = new Vector3().subVectors(rawShapePosition, centerStart);
              let t = shapeToStartVector.dot(wallVector) / wallLengthSq;
              t = Math.max(0, Math.min(1, t));
              snappedShapePosition = new Vector3().copy(centerStart).add(wallVector.clone().multiplyScalar(t));
            }

            if (shapeObject.id === 3) {
              const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.WINDOW_WIDTH);
              const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
              syncDimensionToAttributes(objectAttributes, DefaultAttributeId.WINDOW_WIDTH, finalLength);
              const wallWindow = new WindowModel(snappedShapePosition, finalLength, undefined, undefined, objectAttributes);
              window.push(wallWindow);
            }

            if (shapeObject.id === 4) {
              const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.DOOR_WIDTH);
              const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
              syncDimensionToAttributes(objectAttributes, DefaultAttributeId.DOOR_WIDTH, finalLength);
              const wallDoor = new DoorModel(snappedShapePosition, finalLength, undefined, undefined, undefined, undefined, objectAttributes);
              doors.push(wallDoor);
            }

            if (shapeObject.id === 10) {
              const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.PASSAGE_WIDTH);
              const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
              syncDimensionToAttributes(objectAttributes, DefaultAttributeId.PASSAGE_WIDTH, finalLength);
              const wallPassage = new PassageModel(snappedShapePosition, finalLength, undefined, undefined, objectAttributes);
              passages.push(wallPassage);
            }

            if (shapeObject.id === 5) {
              const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.RADIATOR_LENGTH);
              const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
              const attrDepth = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.RADIATOR_DEPTH);
              const finalDepth = attrDepth !== undefined && attrDepth !== 0 ? attrDepth : DEFAULT_RADIATOR_DEPTH;

              syncDimensionToAttributes(objectAttributes, DefaultAttributeId.RADIATOR_LENGTH, finalLength);
              syncDimensionToAttributes(objectAttributes, DefaultAttributeId.RADIATOR_DEPTH, finalDepth);

              const wallRadiator = new RadiatorModel(snappedShapePosition, finalLength, finalDepth, undefined, objectAttributes);
              radiators.push(wallRadiator);
            }
          });

          const finalWallTypeLabel = wallsAreStandardWalls ? 'Standardwand' : 'Zwischenwand';
          item.wallAttributes.forEach((attr) => {
            if (attr.id === DefaultAttributeId.WALL_TYPE) {
              attr.value = finalWallTypeLabel;
            }
          });

          const wallDepth = item.wallDepth ?? DEFAULT_WALL_DEPTH;
          const newStandardWall = new StandardWallModel(centerStart, centerEnd, wallDepth, window, doors, radiators, item.wallAttributes, item.wall.uuid, passages);

          if (newStandardWall.depth === 0) {
            newStandardWall.depth = wallDepth;
          }

          standardWalls.push(newStandardWall);
        }

        continue;
      }

      // Partition walls are open PanoViewer wall runs; their thickness extends away from the PanoViewer camera origin.
      spaceRelatedWalls.forEach((wall) => {
        const wallRelatedStartEdge = spaceRelatedWallEdges.find((edge) => wall.startEdgeId === edge.uuid);
        const wallRelatedEndEdge = spaceRelatedWallEdges.find((edge) => wall.endEdgeId === edge.uuid);

        if (!wallRelatedStartEdge || !wallRelatedEndEdge) return;

        const wallAttributes = convertAttributes(wall.attributes ?? [], ObjectType.Wall);
        const wallDepth = extractDimensionFromAttributes(wallAttributes, DefaultAttributeId.WALL_THICKNESS);

        const endPosition = new Vector3(wallRelatedStartEdge.floorPosition.x * cameraHeight, -1 * wallRelatedStartEdge.floorPosition.z * cameraHeight, 0);
        const startPosition = new Vector3(wallRelatedEndEdge.floorPosition.x * cameraHeight, -1 * wallRelatedEndEdge.floorPosition.z * cameraHeight, 0);

        const partitionWallDepth = wallDepth ?? DEFAULT_WALL_DEPTH;
        const { centerStartPosition, centerEndPosition, modelStartPosition, modelEndPosition } = calculateCameraAwayPartitionLine(startPosition, endPosition, partitionWallDepth);
        const wallVector = new Vector3().subVectors(centerEndPosition, centerStartPosition);
        const wallLengthSq = wallVector.lengthSq();

        const radiators: Array<RadiatorModel> = [];
        const window: Array<WindowModel> = [];
        const doors: Array<DoorModel> = [];
        const passages: Array<PassageModel> = [];

        wall.shapes?.forEach((shape) => {
          if (!shape.object) return;

          const shapeObject = shape.object as PvCategory;

          let objType: ObjectType | undefined;
          if (shapeObject.id === 3) objType = ObjectType.Window;
          if (shapeObject.id === 4) objType = ObjectType.Door;
          if (shapeObject.id === 5) objType = ObjectType.Radiator;
          if (shapeObject.id === 10) objType = ObjectType.Passage;

          const objectAttributes = convertAttributes(shape.attributes ?? [], objType);

          const rawLength = calculateSmallestWidth(shape.points);
          const scaledLength = rawLength ? rawLength * cameraHeight : undefined;
          const rawCenter = calculateShapeCenterXZ(shape.points);

          const posX = rawCenter ? rawCenter.x * cameraHeight : shape.position.x * cameraHeight;
          const posY = rawCenter ? -1 * rawCenter.z * cameraHeight : -1 * shape.position.z * cameraHeight;
          const rawShapePosition = new Vector3(posX, posY, 0);

          let snappedShapePosition = rawShapePosition.clone();
          if (wallLengthSq > 0) {
            const shapeToStartVector = new Vector3().subVectors(rawShapePosition, centerStartPosition);
            let t = shapeToStartVector.dot(wallVector) / wallLengthSq;
            t = Math.max(0, Math.min(1, t));
            snappedShapePosition = new Vector3().copy(centerStartPosition).add(wallVector.clone().multiplyScalar(t));
          }

          if (shapeObject.id === 3) {
            const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.WINDOW_WIDTH);
            const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
            syncDimensionToAttributes(objectAttributes, DefaultAttributeId.WINDOW_WIDTH, finalLength);
            const wallWindow = new WindowModel(snappedShapePosition, finalLength, undefined, undefined, objectAttributes);
            window.push(wallWindow);
          }

          if (shapeObject.id === 4) {
            const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.DOOR_WIDTH);
            const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
            syncDimensionToAttributes(objectAttributes, DefaultAttributeId.DOOR_WIDTH, finalLength);
            const wallDoor = new DoorModel(snappedShapePosition, finalLength, undefined, undefined, undefined, undefined, objectAttributes);
            doors.push(wallDoor);
          }

          if (shapeObject.id === 10) {
            const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.PASSAGE_WIDTH);
            const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
            syncDimensionToAttributes(objectAttributes, DefaultAttributeId.PASSAGE_WIDTH, finalLength);
            const wallPassage = new PassageModel(snappedShapePosition, finalLength, undefined, undefined, objectAttributes);
            passages.push(wallPassage);
          }

          if (shapeObject.id === 5) {
            const attrLength = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.RADIATOR_LENGTH);
            const finalLength = attrLength !== undefined && attrLength !== 0 ? attrLength : scaledLength;
            const attrDepth = extractDimensionFromAttributes(objectAttributes, DefaultAttributeId.RADIATOR_DEPTH);
            const finalDepth = attrDepth !== undefined && attrDepth !== 0 ? attrDepth : DEFAULT_RADIATOR_DEPTH;

            syncDimensionToAttributes(objectAttributes, DefaultAttributeId.RADIATOR_LENGTH, finalLength);
            syncDimensionToAttributes(objectAttributes, DefaultAttributeId.RADIATOR_DEPTH, finalDepth);

            const wallRadiator = new RadiatorModel(snappedShapePosition, finalLength, finalDepth, undefined, objectAttributes);
            radiators.push(wallRadiator);
          }
        });

        const finalWallTypeLabel = wallsAreStandardWalls ? 'Standardwand' : 'Zwischenwand';
        wallAttributes.forEach((attr) => {
          if (attr.id === DefaultAttributeId.WALL_TYPE) {
            attr.value = finalWallTypeLabel;
          }
        });

        const newPartitionWall = new PartitionWallModel(modelStartPosition, modelEndPosition, partitionWallDepth, window, doors, radiators, wallAttributes, undefined, passages);
        partitionWalls.push(newPartitionWall);
      });
    }
  }

  const walls = [...standardWalls, ...partitionWalls];
  return {
    walls,
    standardWalls,
    partitionWalls,
    roomAttributes,
    floorAttributes,
    cameraOffset: new Vector3(),
    boundaryPoints: collectedBoundaryPoints,
  };
}
